/**
 * ChangelogPanel — Bell-Icon in der Top-Nav + Slide-out Panel mit Updates.
 *
 * UX:
 *   - Bell-Icon mit kleinem emerald-Pulsing-Counter (unseen items)
 *   - Click → öffnet Sheet (von rechts)
 *   - Liste chronologisch absteigend
 *   - Pro Item: Badge (Neu/Verbessert/Behoben), Datum, Titel, Beschreibung, Kategorie-Pill
 *   - „Alle als gelesen markieren"-Button
 *   - Optional „Mehr erfahren"-Link → in-App-Navigation
 *
 * Verwendung in TopNav:
 *   <ChangelogButton />
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import useChangelog from '@/hooks/useChangelog';
import CHANGELOG, { CATEGORY_LABELS, BADGE_STYLES } from '@/data/changelog';

function CategoryPill({ category }) {
  const label = CATEGORY_LABELS[category] || category;
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
      {label}
    </span>
  );
}

function BadgePill({ badge }) {
  if (!badge) return null;
  const cls = BADGE_STYLES[badge] || 'bg-slate-100 text-slate-700 border-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>
      {badge}
    </span>
  );
}

function ChangelogItem({ item, isUnseen, onClickLink }) {
  const dateStr = new Date(item.date).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  return (
    <div
      className={`relative rounded-xl border p-4 ${
        isUnseen ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white'
      }`}
      data-testid={`changelog-item-${item.id}`}
    >
      {isUnseen && (
        <span className="absolute -top-1.5 -right-1.5 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" aria-label="ungelesen" />
      )}
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <BadgePill badge={item.badge} />
        <CategoryPill category={item.category} />
        <span className="text-xs text-slate-400">{dateStr}</span>
      </div>
      <h3
        className="text-base font-semibold tracking-tight text-slate-900 mb-1"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        {item.title}
      </h3>
      <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
      {item.link && (
        <Link
          to={item.link}
          onClick={() => onClickLink?.(item.id)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700"
          data-testid={`changelog-item-link-${item.id}`}
        >
          Direkt ausprobieren →
        </Link>
      )}
    </div>
  );
}

export function ChangelogButton({ className = '', audience = 'fl' }) {
  const [open, setOpen] = useState(false);
  // Snapshot der beim Öffnen ungelesenen IDs — Items behalten ihr „Neu"-Highlight,
  // während der Badge-Zähler sofort verschwindet (und gespeichert bleibt).
  const [sessionUnseen, setSessionUnseen] = useState([]);
  const { items, unseenItems, unseenCount, markAllSeen, markSeen } = useChangelog(audience);

  const openPanel = () => {
    setSessionUnseen(unseenItems.map((i) => i.id));
    setOpen(true);
    // Beim Öffnen alles als gelesen markieren → Zähler weg, persistiert (localStorage)
    if (unseenItems.length > 0) markAllSeen();
  };

  const onLinkClick = (id) => {
    markSeen(id);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={openPanel}
        className={`relative rounded-full p-2 hover:bg-slate-100 transition-colors ${className}`}
        aria-label="Was ist neu"
        data-testid="changelog-trigger"
      >
        <Bell className="h-5 w-5 text-slate-600" />
        {unseenCount > 0 && (
          <span
            className="absolute top-0.5 right-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold text-white animate-pulse"
            data-testid="changelog-unseen-count"
          >
            {unseenCount > 9 ? '9+' : unseenCount}
          </span>
        )}
      </button>

      {/* Slide-out Panel — via Portal direkt in <body>:
          Header-Container mit backdrop-blur/transform bilden sonst einen
          Containing Block für position:fixed → Panel wäre abgeschnitten
          (Bug im SB-Portal, Juni 2026). */}
      {open && createPortal(
        <>
          <div
            className="fixed inset-0 z-[90] bg-slate-900/30"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="fixed right-0 top-0 z-[100] h-full w-full max-w-md bg-white shadow-2xl flex flex-col"
            data-testid="changelog-panel"
            role="dialog"
            aria-modal="true"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-600" />
                <h2 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Was ist neu?
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-1.5 hover:bg-slate-100"
                aria-label="Schließen"
                data-testid="changelog-close"
              >
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {items.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Noch keine Updates.</p>
                </div>
              ) : (
                items.map((item) => (
                  <ChangelogItem
                    key={item.id}
                    item={item}
                    isUnseen={sessionUnseen.includes(item.id)}
                    onClickLink={onLinkClick}
                  />
                ))
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 px-5 py-3 text-center text-xs text-slate-400">
              ivoliq wird kontinuierlich weiterentwickelt — Feedback gerne über das Feedback-Modul.
            </div>
          </aside>
        </>,
        document.body
      )}
    </>
  );
}

export default ChangelogButton;
